import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";

export const AuthModal = () => {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register, formatApiErrorDetail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (mode) => {
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back, trader.");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Account created — your edge is live.");
      }
      setAuthOpen(false);
      setForm({ name: "", email: "", password: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => setForm({ name: "", email: "trader@demo.com", password: "Demo@12345" });

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="qs-card border-slate-700 text-slate-100 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">
            Access the <span className="text-gradient">AI Signal Desk</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Join 12,000+ traders following verified 90% win-rate signals.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={authMode} onValueChange={setAuthMode} className="w-full">
          <TabsList className="grid grid-cols-2 bg-slate-800/60">
            <TabsTrigger value="login" data-testid="auth-tab-login">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="login-email-input" type="email" value={form.email} onChange={upd("email")} placeholder="you@email.com" className="bg-slate-900/60 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="login-password-input" type="password" value={form.password} onChange={upd("password")} placeholder="••••••••" className="bg-slate-900/60 border-slate-700" />
            </div>
            <Button data-testid="auth-login-submit-button" disabled={loading} onClick={() => submit("login")} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Login to Dashboard"}
            </Button>
            <Button data-testid="auth-demo-button" variant="outline" onClick={fillDemo} className="w-full border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-800">
              <Zap size={16} className="mr-2 text-cyan-400" /> Use demo account
            </Button>
          </TabsContent>

          <TabsContent value="register" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input data-testid="register-name-input" value={form.name} onChange={upd("name")} placeholder="Alex Trader" className="bg-slate-900/60 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={upd("email")} placeholder="you@email.com" className="bg-slate-900/60 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="register-password-input" type="password" value={form.password} onChange={upd("password")} placeholder="min 6 characters" className="bg-slate-900/60 border-slate-700" />
            </div>
            <Button data-testid="auth-register-submit-button" disabled={loading} onClick={() => submit("register")} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Create free account"}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
